/**
 * Secure Storage Utility
 *
 * Platform strategy:
 *  - Native (Android/iOS): Capacitor Preferences
 *  - Web: not used for auth tokens
 */

/** Returns true when running inside a Capacitor native app (Android / iOS). */
export const isNative = (): boolean =>
  typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.();

async function loadPreferencesModule() {
  // Important: return the module object, not the plugin proxy itself.
  // If an async function returns the proxy, the Promise resolution path may
  // inspect `.then`, which Capacitor treats as a plugin method call on Android.
  // @ts-ignore Capacitor is only used at runtime on native.
  return import('@capacitor/preferences');
}

/**
 * Persist a string value under `key` in native storage.
 * Must only be called on native platforms.
 */
export const setNativeItem = async (key: string, value: string): Promise<void> => {
  const { Preferences } = await loadPreferencesModule();
  await Preferences.set({ key, value });
  console.log('[SecureStorage] Native set complete.', {
    key,
    valueLength: value.length,
  });
};

/**
 * Retrieve a string value from native storage.
 * Returns `null` when the key does not exist.
 * Must only be called on native platforms.
 */
export const getNativeItem = async (key: string): Promise<string | null> => {
  const { Preferences } = await loadPreferencesModule();
  const { value } = await Preferences.get({ key });
  console.log('[SecureStorage] Native get complete.', {
    key,
    hasValue: !!value,
    valueLength: value?.length ?? 0,
  });
  return value ?? null;
};

/**
 * Remove a key from native storage.
 * Must only be called on native platforms.
 */
export const removeNativeItem = async (key: string): Promise<void> => {
  const { Preferences } = await loadPreferencesModule();
  await Preferences.remove({ key });
  console.log('[SecureStorage] Native remove complete.', { key });
};
